import axios from 'axios';

/**
 * Axios API Client with Auth Integration
 * Automatically includes credentials (cookies) in requests
 */
const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || 'http://localhost:4000';
const AUTH_UI_URL = process.env.REACT_APP_AUTH_UI_URL || 'http://localhost:5173';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1',
  timeout: 30000,
  withCredentials: true, // Important: Send cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

const sleep = (ms = 50) => new Promise((resolve) => setTimeout(resolve, ms));

const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : null;
};

const redirectToLogin = () => {
  if (typeof window === 'undefined') return;
  const currentUrl = window.location.href;
  const redirect = `${AUTH_UI_URL}/login?redirect=${encodeURIComponent(currentUrl)}`;
  console.warn('[CRM apiClient] Redirecting to Auth UI:', redirect);
  window.location.href = redirect;
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = readCookie('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.debug('[CRM apiClient] Request', {
        url: config.url,
        method: config.method,
        hasToken: !!token,
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let refreshPromise = null;

const performRefresh = async (attempt = 1) => {
  try {
    if (process.env.NODE_ENV !== 'production') {
      console.debug(`[CRM apiClient] Attempting refresh (attempt ${attempt})`);
    }

    await axios.post(`${AUTH_SERVICE_URL}/api/auth/refresh`, {}, { withCredentials: true });
    // wait a tick for cookies to update
    await sleep(50);
    return true;
  } catch (err) {
    const status = err.response?.status;
    console.error(`[CRM apiClient] Refresh token error (attempt ${attempt})`, status, err.message);

    if ((status === 400 || status === 401) && attempt < 3) {
      // Another app may have just rotated the cookie; wait and try once more.
      await sleep(200);
      return performRefresh(attempt + 1);
    }

    return false;
  }
};

const refreshAuthToken = () => {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        const originalRequest = error.config;
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          const refreshed = await refreshAuthToken();
          if (refreshed) {
            if (process.env.NODE_ENV !== 'production') {
              console.debug('[CRM apiClient] Replaying request after refresh', originalRequest.url);
            }
            return apiClient(originalRequest);
          }
        }

        redirectToLogin();
        return Promise.reject(error);
      }

      // Handle 403 Forbidden
      if (error.response.status === 403) {
        // User doesn't have CRM access
        window.location.href = `${AUTH_UI_URL}/unauthorized?app=CRM`;
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
